import {
  Box,
  Flex,
  HStack,
  Text,
  VStack,
  useMediaQuery,
  Pressable,
  Avatar,
  Checkbox
} from 'native-base';
import { useState, useEffect, useCallback, useRef } from 'react';
import { COLORS } from '../../../../utils/colors';
import { MOBILE_VIEW_WIDTH } from '../../../../utils/screens';
import { COMMONFONTSIZE } from '../../../../utils/fonts';
import CustomModal from '../../../../components/modal';
import CustomButton from '../../../../components/button';
import InputField from '../../../../components/input';
import { deleteCardsIconSVG } from '../../../../utils/svg';
import FileUploader from '../../../../containers/fileUpload';
import ViewUploadImageCards from '../../../../containers/viewUploadImageCards';
import { uploadFileAPI } from '../../../../api/apis/workflow';
import { Toaster } from '../../../../components/toaster';

const EventPartnerModal = ({
  isOpen,
  onClose,
  type = 'event',
  onSubmit,
  editData = null
}) => {
  const [MobileWindow] = useMediaQuery({ maxWidth: MOBILE_VIEW_WIDTH });
  const previousEditData = useRef(editData);
  const isAddMode = !editData;
  const coordinatorIdRef = useRef(0); // For generating unique IDs

  // Get initial state with unique IDs
  const getInitialState = useCallback(
    () => ({
      agency: '',
      coordinators: [
        {
          id: coordinatorIdRef.current++,
          contactName: '',
          emailId: '',
          contact_number: '',
          is_quotation_submit: false
        },
        {
          id: coordinatorIdRef.current++,
          contactName: '',
          emailId: '',
          contact_number: '',
          is_quotation_submit: false
        },
        {
          id: coordinatorIdRef.current++,
          contactName: '',
          emailId: '',
          contact_number: '',
          is_quotation_submit: false
        }
      ],
      errors: {},
      showErrors: false
    }),
    []
  );

  // Initialize state
  const [agency, setAgency] = useState('');
  const [gstNo, setGstNo] = useState('');
  const [logoFiles, setLogoFiles] = useState([]);
  const [selectedLogoFiles, setSelectedLogoFiles] = useState([]);
  const [coordinators, setCoordinators] = useState([
    {
      id: coordinatorIdRef.current++,
      contactName: '',
      emailId: '',
      contact_number: '',
      is_quotation_submit: false
    },
    {
      id: coordinatorIdRef.current++,
      contactName: '',
      emailId: '',
      contact_number: '',
      is_quotation_submit: false
    },
    {
      id: coordinatorIdRef.current++,
      contactName: '',
      emailId: '',
      contact_number: '',
      is_quotation_submit: false
    }
  ]);
  const [errors, setErrors] = useState({});
  const [showErrors, setShowErrors] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const toaster = Toaster();

  // Handle logo file selection
  const handleLogoFilesSelect = (files) => {
    if (files && files.length > 0) {
      // Convert File objects to URLs for display
      const imageUrls = Array.from(files).map((file) =>
        file instanceof File ? URL.createObjectURL(file) : file
      );
      setSelectedLogoFiles(imageUrls);
      setLogoFiles(files);
    } else {
      setSelectedLogoFiles([]);
      setLogoFiles([]);
    }
  };

  const handleDeleteLogo = () => {
    // Revoke object URLs to prevent memory leaks
    selectedLogoFiles.forEach((url) => {
      if (url && url.startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
    });
    setSelectedLogoFiles([]);
    setLogoFiles([]);
  };

  // Force reset function that definitely clears everything
  const forceReset = useCallback(() => {
    const initialState = getInitialState();
    setAgency(initialState.agency);
    setGstNo('');
    setLogoFiles([]);
    setSelectedLogoFiles([]);
    setCoordinators([...initialState.coordinators]);
    setErrors({});
    setShowErrors(false);
    setIsInitialized(false);
  }, [getInitialState]);

  // Function to populate form with edit data
  const populateEditData = useCallback(() => {
    if (!editData) return;

    const agencyName = editData.agency || '';
    setAgency(agencyName);
    setGstNo(editData.gst_number || editData.gstNo || '');
    
    // Handle logo if it exists in editData
    if (editData.logo) {
      // If logo is a string (file_name from API), use it directly
      if (typeof editData.logo === 'string') {
        setSelectedLogoFiles([editData.logo]);
      } else if (Array.isArray(editData.logo) && editData.logo.length > 0) {
        const logoItem = editData.logo[0];
        if (typeof logoItem === 'string') {
          setSelectedLogoFiles([logoItem]);
        } else if (logoItem && logoItem.file_name) {
          setSelectedLogoFiles([logoItem.file_name]);
        }
      }
    }

    const contactDetails = editData.contactDetails || [];

    // Create default structure with 3 coordinators with unique IDs
    const defaultCoordinators = [
      {
        id: coordinatorIdRef.current++,
        contactName: '',
        emailId: '',
        contact_number: '',
        is_quotation_submit: false
      },
      {
        id: coordinatorIdRef.current++,
        contactName: '',
        emailId: '',
        contact_number: '',
        is_quotation_submit: false
      },
      {
        id: coordinatorIdRef.current++,
        contactName: '',
        emailId: '',
        contact_number: '',
        is_quotation_submit: false
      }
    ];

    // Merge existing data with default structure
    const mergedCoordinators = defaultCoordinators.map(
      (defaultCoord, index) => {
        if (contactDetails[index]) {
          return {
            ...defaultCoord,
            contactName: contactDetails[index].contactName || '',
            emailId: Array.isArray(contactDetails[index].emailId)
              ? contactDetails[index].emailId[0] || ''
              : contactDetails[index].emailId || '',
            contact_number: contactDetails[index].contact_number || '',
            is_quotation_submit: contactDetails[index].is_quotation_submit || false
          };
        }
        return defaultCoord;
      }
    );

    // Add any additional coordinators beyond the first 3
    if (contactDetails.length > 3) {
      contactDetails.slice(3).forEach((coord) => {
        mergedCoordinators.push({
          id: coordinatorIdRef.current++,
          contactName: coord.contactName || '',
          emailId: Array.isArray(coord.emailId)
            ? coord.emailId[0] || ''
            : coord.emailId || '',
          contact_number: coord.contact_number || '',
          is_quotation_submit: coord.is_quotation_submit || false
        });
      });
    }

    setCoordinators(mergedCoordinators);
    setErrors({});
    setShowErrors(false);
  }, [editData]);

  // Main effect to handle modal opening
  useEffect(() => {
    if (isOpen) {
      setIsInitialized(false);

      if (isAddMode) {
        // Add mode - always start fresh
        forceReset();
        setTimeout(() => {
          setIsInitialized(true);
        }, 50);
      } else {
        // Edit mode - populate with data
        populateEditData();
        setTimeout(() => {
          setIsInitialized(true);
        }, 50);
      }
    }
  }, [isOpen, isAddMode, forceReset, populateEditData]);

  // Handle mode changes (switching between add and edit)
  useEffect(() => {
    const hasEditDataChanged = previousEditData.current !== editData;
    previousEditData.current = editData;

    if (hasEditDataChanged && isOpen) {
      setIsInitialized(false);

      if (isAddMode) {
        forceReset();
      } else {
        populateEditData();
      }

      setTimeout(() => {
        setIsInitialized(true);
      }, 50);
    }
  }, [editData, isOpen, isAddMode, forceReset, populateEditData]);

  // Reset when modal closes (for add mode)
  useEffect(() => {
    if (!isOpen && isAddMode) {
      const timeoutId = setTimeout(() => {
        forceReset();
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, isAddMode, forceReset]);

  // Enhanced onClose handler
  const handleClose = useCallback(() => {
    // Revoke object URLs to prevent memory leaks
    selectedLogoFiles.forEach((url) => {
      if (url && url.startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
    });
    
    // Immediately reset if add mode
    if (isAddMode) {
      forceReset();
    }

    onClose();
  }, [isAddMode, onClose, forceReset, selectedLogoFiles]);

  const addCoordinator = () => {
    setCoordinators([
      ...coordinators,
      {
        id: coordinatorIdRef.current++,
        contactName: '',
        emailId: '',
        contact_number: '',
        is_quotation_submit: false
      }
    ]);
  };

  const removeCoordinator = (coordinatorId) => {
    if (coordinators.length > 3) {
      const updated = coordinators.filter(
        (coord) => coord.id !== coordinatorId
      );
      setCoordinators(updated);
    }
  };

  const updateCoordinator = (coordinatorId, field, value) => {
    const updated = coordinators.map((coord) =>
      coord.id === coordinatorId ? { ...coord, [field]: value } : coord
    );
    setCoordinators(updated);
  };

  // Upload logo files to server
  const uploadFilesToServer = async (data, type) => {
    let docs = [];
    if (data && data.length && data.length > 0) {
      try {
        let res = await uploadFileAPI(data, 'image', 'image');

        if (res && Array.isArray(res)) {
          res.map((item) => {
            if (item && item.error) {
              toaster('Upload file failed', 'error', 'error');
              return;
            }
            if (item && item.data && item.data.data && item.data.data.data) {
              docs.push({
                doc_type: item.data.data.data.doc_type,
                file_size: item.data.data.data.file_size.toString(),
                file_name: item.data.data.data.file_name,
              });
            }
          });
        }
      } catch (error) {
        console.error('Upload error:', error);
        toaster('Upload failed', 'error', 'error');
      }
    }
    return docs;
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    // Agency validation (always required)
    if (!agency.trim()) {
      newErrors.agency = 'Agency name is required';
      isValid = false;
    } else if (agency.length < 3) {
      newErrors.agency = 'Agency name must be at least 3 characters';
      isValid = false;
    }

    // GST No validation (mandatory)
    if (!gstNo.trim()) {
      newErrors.gstNo = 'GST No is required';
      isValid = false;
    } else if (gstNo.trim().length < 3) {
      newErrors.gstNo = 'GST No must be at least 3 characters';
      isValid = false;
    }

    // Validate first 3 coordinators (required)
    coordinators.slice(0, 3).forEach((coordinator, index) => {
      if (!coordinator.contactName.trim()) {
        newErrors[`name_${coordinator.id}`] = 'Name is required';
        isValid = false;
      }

      if (!coordinator.emailId.trim()) {
        newErrors[`email_${coordinator.id}`] = 'Email is required';
        isValid = false;
      } else if (!/^\S+@\S+\.\S+$/.test(coordinator.emailId)) {
        newErrors[`email_${coordinator.id}`] = 'Invalid email format';
        isValid = false;
      }

      if (!coordinator.contact_number.trim()) {
        newErrors[`phone_${coordinator.id}`] = 'Phone is required';
        isValid = false;
      } else if (!/^\d{10}$/.test(coordinator.contact_number)) {
        newErrors[`phone_${coordinator.id}`] = 'Phone must be 10 digits';
        isValid = false;
      }
    });

    // Additional coordinators (optional)
    coordinators.slice(3).forEach((coordinator) => {
      if (
        coordinator.contactName.trim() ||
        coordinator.emailId.trim() ||
        coordinator.contact_number.trim()
      ) {
        if (coordinator.contactName.trim() && !coordinator.emailId.trim()) {
          newErrors[`email_${coordinator.id}`] =
            'Email is required if name is provided';
          isValid = false;
        }
        if (
          coordinator.emailId.trim() &&
          !/^\S+@\S+\.\S+$/.test(coordinator.emailId)
        ) {
          newErrors[`email_${coordinator.id}`] = 'Invalid email format';
          isValid = false;
        }
        if (
          coordinator.contact_number.trim() &&
          !/^\d{10}$/.test(coordinator.contact_number)
        ) {
          newErrors[`phone_${coordinator.id}`] = 'Phone must be 10 digits';
          isValid = false;
        }
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async () => {
    const isValid = validateForm();
    setShowErrors(true);

    if (isValid) {
      let logoFileName = '';
      
      // Upload logo if files are selected
      if (logoFiles && logoFiles.length > 0) {
        const uploadedDocs = await uploadFilesToServer(logoFiles, 'logo');
        if (uploadedDocs && uploadedDocs.length > 0) {
          // Extract file_name as string (API expects string, not array)
          logoFileName = uploadedDocs[0].file_name || '';
        }
      } else if (!isAddMode && selectedLogoFiles && selectedLogoFiles.length > 0) {
        // In edit mode, if no new file selected, preserve existing logo
        const existingLogo = selectedLogoFiles[0];
        // If it's not a blob URL (newly selected file), it's the existing logo
        if (existingLogo && !existingLogo.startsWith('blob:')) {
          logoFileName = existingLogo;
        }
      }

      onSubmit({
        agency,
        gst_number: gstNo.trim(),
        logo: logoFileName, // Pass as string, not array
        contactDetails: coordinators
          .filter(
            (coord) =>
              coord.contactName.trim() ||
              coord.emailId.trim() ||
              coord.contact_number.trim()
          )
          .map(({ id, ...coord }) => ({
            ...coord,
            emailId: coord.emailId ? [coord.emailId] : [],
            is_quotation_submit: coord.is_quotation_submit || false
          })) // emailId as array
      });

      // Reset form after successful submission in add mode
      if (isAddMode) {
        forceReset();
      }

      onClose();
    }
  };

  const shouldShowError = (coordinator, field) => {
    if (!showErrors) return false;

    const coordinatorIndex = coordinators.findIndex(
      (coord) => coord.id === coordinator.id
    );

    if (coordinatorIndex < 3) return !!errors[`${field}_${coordinator.id}`];

    if (
      coordinator.contactName.trim() ||
      coordinator.emailId.trim() ||
      coordinator.contact_number.trim()
    ) {
      return !!errors[`${field}_${coordinator.id}`];
    }
    return false;
  };

  const modalBody = () => (
    <Box>
      <InputField
        label='Agency Name *'
        value={agency}
        onChange={(e) => setAgency(e.target.value)}
        pt={0}
        mb={4}
        isInvalid={showErrors && !!errors.agency}
        errorMessage={showErrors ? errors.agency : ''}
      />

      {/* GST No and Upload Logo Section */}
      <VStack space={3} mb={4}>
      <InputField
        label='GST No. *'
        value={gstNo}
        onChange={(e) => setGstNo(e.target.value)}
        placeholder="Enter GST number"
        isInvalid={showErrors && !!errors.gstNo}
        errorMessage={showErrors ? errors.gstNo : ''}
      />

        <Box>
          <Text
            fontSize="sm"
            fontWeight="medium"
            mb={2}
          >
            Upload Logo
          </Text>
          <FileUploader
            files={logoFiles}
            show={false}
            setFiles={handleLogoFilesSelect}
            accept={["png", "jpg", "jpeg", "jfif"]}
            types={".png, .jpg, .jpeg, .jfif"}
            forceRemount={isInitialized}
          />
          <HStack flex={1} flexWrap={"wrap"} mt={2}>
            {selectedLogoFiles.length > 0 && selectedLogoFiles[0] !== "" && (
              <ViewUploadImageCards
                ind={0}
                url={selectedLogoFiles[0]}
                onDeleteImage={handleDeleteLogo}
              />
            )}
          </HStack>
        </Box>
      </VStack>

      {coordinators.map((coordinator, index) => (
        <Box
          key={coordinator.id} // Use unique ID as key
          borderWidth={1}
          borderColor={COLORS.SOLID_DIVIDER_COLOR}
          borderRadius={8}
          p={4}
          mb={4}
          mt={4}
        >
          <HStack
            justifyContent='space-between'
            alignItems='center'
            mb={3}
          >
            <Text fontWeight='bold'>
              Contact Person Detail {index + 1}
              {index < 3 && ' *'}
            </Text>
            <Pressable
              onPress={() => removeCoordinator(coordinator.id)}
              disabled={coordinators.length <= 3}
              opacity={coordinators.length <= 3 ? 0.5 : 1}
            >
              <Avatar
                bg={'none'}
                size={'xs'}
              >
                {deleteCardsIconSVG}
              </Avatar>
            </Pressable>
          </HStack>

          <VStack space={3}>
            <InputField
              label='Name'
              value={coordinator.contactName}
              onChange={(e) =>
                updateCoordinator(coordinator.id, 'contactName', e.target.value)
              }
              isInvalid={shouldShowError(coordinator, 'name')}
              errorMessage={showErrors ? errors[`name_${coordinator.id}`] : ''}
            />

            <InputField
              label='Email'
              value={coordinator.emailId}
              onChange={(e) =>
                updateCoordinator(coordinator.id, 'emailId', e.target.value)
              }
              isInvalid={shouldShowError(coordinator, 'email')}
              errorMessage={showErrors ? errors[`email_${coordinator.id}`] : ''}
            />

            <InputField
              label='Phone'
              value={coordinator.contact_number}
              onChange={(e) =>
                updateCoordinator(
                  coordinator.id,
                  'contact_number',
                  e.target.value
                )
              }
              maxLength={10}
              isInvalid={shouldShowError(coordinator, 'phone')}
              errorMessage={showErrors ? errors[`phone_${coordinator.id}`] : ''}
            />

            <Box 
              mt={3}
              p={3}
              bg={coordinator.is_quotation_submit ? 'rgba(243, 112, 50, 0.08)' : COLORS.BADGE_GRAY_COLOR}
              borderRadius={8}
              borderWidth={1}
              borderColor={coordinator.is_quotation_submit ? COLORS.THEME_COLOR : COLORS.BORDER_COLOR}
              transition="all 0.2s"
            >
              <HStack space={2} alignItems="center">
                <Checkbox
                  isChecked={coordinator.is_quotation_submit || false}
                  onChange={() =>
                    updateCoordinator(coordinator.id, 'is_quotation_submit', !coordinator.is_quotation_submit)
                  }
                  value="is_quotation_submit"
                  size="md"
                  _checked={{
                    bg: COLORS.THEME_COLOR,
                    borderColor: COLORS.THEME_COLOR,
                    _hover: {
                      bg: COLORS.THEME_COLOR,
                      borderColor: COLORS.THEME_COLOR,
                    },
                    _pressed: {
                      bg: COLORS.THEME_COLOR,
                      borderColor: COLORS.THEME_COLOR,
                    },
                  }}
                  _icon={{
                    color: COLORS.WHITE_COLOR,
                  }}
                  borderColor={COLORS.BORDER_COLOR}
                  _hover={{
                    borderColor: COLORS.THEME_COLOR,
                  }}
                />
                <Text 
                  fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
                  fontWeight={coordinator.is_quotation_submit ? '600' : '400'}
                  color={coordinator.is_quotation_submit ? COLORS.THEME_COLOR : COLORS.ACCORDION_TITLE_COLOR}
                >
                  Would you like to provide Quotation Submission Access?
                </Text>
              </HStack>
            </Box>
          </VStack>
        </Box>
      ))}
    </Box>
  );

  const modalFooter = () => (
    <Flex
      direction='row'
      justifyContent='space-between'
      width='100%'
    >
      <CustomButton
        onPress={addCoordinator}
        text='+ Add New Contact Person'
        variant='outline'
      />

      <HStack space={2}>
        <CustomButton
          onPress={handleClose}
          text='Cancel'
          variant='outline'
        />
        <CustomButton
          onPress={handleSubmit}
          text={editData ? 'Update' : 'Create'}
        />
      </HStack>
    </Flex>
  );

  // Don't render the modal content until it's properly initialized
  if (isOpen && !isInitialized) {
    return null;
  }

  return (
    <CustomModal
      key={
        isAddMode
          ? 'add-mode'
          : `edit-${editData?.id || editData?.agency || 'unknown'}`
      }
      isOpen={isOpen}
      onClose={handleClose}
      header={`${editData ? 'Edit' : 'Add'} ${
        type === 'mice' ? 'MICE' : 'Event'
      } Partner`}
      body={modalBody()}
      footer={modalFooter()}
      width={MobileWindow ? '100%' : '60%'}
      maxH={MobileWindow ? '80vh' : undefined}
    />
  );
};

export default EventPartnerModal;
